import React, { useState } from 'react';
import {
  PRIVACY_POLICY_ACCEPTED_KEY,
  PRIVACY_POLICY_VERSION_KEY,
  TERMS_ACCEPTED_KEY,
  TERMS_VERSION_KEY,
  CURRENT_POLICY_VERSION,
  CONSENTS_ACCEPT,
} from '../services/policyService';
import localStorageService from '../services/localStorageService';
import api from '../utils/api';
import eventEmitter from '../utils/eventEmitter';
import TermsConditionsModel from './TermsConditionsModel';

interface PrivacyPolicyBannerProps {
  onAccepted?: () => void;
  onClose?: () => void;
}

const PrivacyPolicyBanner: React.FC<PrivacyPolicyBannerProps> = ({ onAccepted, onClose }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  const handleAccept = async () => {
    setIsLoading(true);
    try {
      const userInfo = localStorageService.getUserInfo();
      const token = userInfo?.access_token || '';

      if (token) {
        await api.post(
          CONSENTS_ACCEPT,
          {
            privacyPolicyVersion: CURRENT_POLICY_VERSION,
            termsVersion: CURRENT_POLICY_VERSION,
          },
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );
      }

      localStorage.setItem(PRIVACY_POLICY_ACCEPTED_KEY, 'true');
      localStorage.setItem(PRIVACY_POLICY_VERSION_KEY, CURRENT_POLICY_VERSION);
      localStorage.setItem(TERMS_ACCEPTED_KEY, 'true');
      localStorage.setItem(TERMS_VERSION_KEY, CURRENT_POLICY_VERSION);

      eventEmitter.emit('privacy_policy_accepted');
      onAccepted?.();
    } catch (error) {
      localStorage.setItem(PRIVACY_POLICY_ACCEPTED_KEY, 'true');
      localStorage.setItem(PRIVACY_POLICY_VERSION_KEY, CURRENT_POLICY_VERSION);
      localStorage.setItem(TERMS_ACCEPTED_KEY, 'true');
      localStorage.setItem(TERMS_VERSION_KEY, CURRENT_POLICY_VERSION);

      eventEmitter.emit('privacy_policy_accepted');
      onAccepted?.();
    } finally {
      setIsLoading(false);
    }
  };

  const handleReadTerms = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowTermsModal(true);
  };

  return (
    <>
      <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-center justify-center">
        <div className="relative w-[50vw] max-w-2xl h-[80vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col animate-scaleIn">

          <div className="px-8 py-6 border-b bg-gray-50">
            <h2 className="text-2xl font-bold text-gray-900">Privacy & Terms</h2>
            <p className="text-sm text-gray-500 mt-1">Please review and accept before continuing</p>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-4">
            <div className="max-w-4xl mx-auto">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Welcome to Our Services</h3>

              <p className="text-sm text-gray-700 leading-7 mb-4">
                Thanks for using our products and services ("Services"). The Services are provided by our company.
              </p>

              <p className="text-sm text-gray-700 leading-7 mb-4">
                By using our Services, you are agreeing to these terms. Please read them carefully.
              </p>

              <p className="text-sm text-gray-700 leading-7 mb-4">
                Our Services are very diverse, so sometimes additional terms or product requirements may apply.
                Additional terms will be available with the relevant Services, and those additional terms become part
                of your agreement with us if you use those Services.
              </p>

              <h4 className="text-base font-semibold text-gray-900 mt-6 mb-3">Using our Services</h4>

              <p className="text-sm text-gray-700 leading-7 mb-4">
                You must follow any policies made available to you within the Services.
              </p>

              <p className="text-sm text-gray-700 leading-7 mb-4">
                Don't misuse our Services. For example, don't interfere with our Services or try to access them using a
                method other than the interface and the instructions that we provide.
              </p>

              <button
                onClick={handleReadTerms}
                className="text-blue-600 hover:text-blue-700 underline text-sm"
              >
                Read our Terms and Conditions
              </button>
            </div>
          </div>

          <div className="border-t bg-white px-8 py-5 flex flex-col items-center gap-3">
            <button
              onClick={handleAccept}
              disabled={isLoading}
              className="min-w-[260px] px-6 py-3 rounded-full border-2 border-gray-800 text-gray-800 font-semibold hover:bg-gray-900 hover:text-white transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Accepting...
                </>
              ) : (
                'I have read and accept the terms of service.'
              )}
            </button>

            <button
              onClick={() => onClose?.()}
              className="px-4 py-1.5 text-sm text-gray-600 hover:text-gray-800 font-medium rounded-md focus:outline-none focus:ring-1 focus:ring-gray-300"
            >
              Cancel
            </button>
          </div>
        </div>

        <style>{`
          @keyframes fadeSlideIn {
            from {
              opacity: 0;
              transform: scale(0.96);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }
          .animate-scaleIn {
            animation: fadeSlideIn 0.3s ease-out;
          }
        `}</style>
      </div>

      {/* Terms Modal - Opens on top of Privacy Policy Banner */}
      {showTermsModal && (
        <TermsConditionsModel
          onClose={() => setShowTermsModal(false)}
          onAccept={handleAccept}
          isLoading={isLoading}
        />
      )}
    </>
  );
};

export default PrivacyPolicyBanner;